import { Box, Dialog, DialogContent, Typography } from "@mui/material"
import { BoxFC } from "components/BoxCustom"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import { addIdForDataGrid } from "utils"
import { existingMappingsColumns, unmappedExpensesColumns } from "./expenseMappingColumns"
import React, { useCallback, useMemo, useState } from "react"

const ExpenseMappingDialog = ({ 
  dialogOpen, 
  setDialogOpen, 
  unmappedExpenses = [], 
  availableExpenses = [], 
  existingMappings = [],
  sourceSiteAbbr = '',
  onSave,
  onRemoveMapping
}) => {
  const [mappings, setMappings] = useState({});

  // Initialize mappings when dialog opens
  React.useEffect(() => {
    if (dialogOpen && unmappedExpenses.length > 0) {
      const initialMappings = {};
      unmappedExpenses.forEach(exp => {
        // Try to find auto-match by name
        const autoMatch = availableExpenses.find(avail => 
          avail.ExpNm === exp.expNm || (avail.ExpNmEn && avail.ExpNmEn === exp.expNmEn)
        );
        initialMappings[exp.sourceExpId] = autoMatch?.ExpId || '';
      });
      setMappings(initialMappings);
    }
  }, [dialogOpen, unmappedExpenses, availableExpenses]);

  const handleMappingChange = useCallback((sourceExpId, currentExpId) => {
    setMappings(prev => ({ ...prev, [sourceExpId]: currentExpId }));
  }, []);

  const handleSave = useCallback(() => {
    const mappingData = unmappedExpenses
      .filter(exp => mappings[exp.sourceExpId])
      .map(exp => {
        const selectedMapping = mappings[exp.sourceExpId];
        
        if (selectedMapping === 'CREATE_NEW') {
          return {
            sourceExpId: exp.sourceExpId,
            sourceExpName: exp.expNm,
            sourceExpNameEn: exp.expNmEn,
            currentExpId: 'CREATE_NEW',
            currentExpName: `สร้างใหม่: ${exp.expNm}`,
            createNew: true
          };
        } else {
          return {
            sourceExpId: exp.sourceExpId,
            sourceExpName: exp.expNm,
            sourceExpNameEn: exp.expNmEn,
            currentExpId: selectedMapping,
            currentExpName: availableExpenses.find(a => a.ExpId === selectedMapping)?.ExpNm || '',
            createNew: false
          };
        }
      });

    if (mappingData.length === 0) {
      alert('กรุณาเลือกการแมปอย่างน้อย 1 รายการ');
      return;
    }

    onSave(mappingData);
  }, [mappings, unmappedExpenses, availableExpenses, onSave]);

  const existingMappingsColumnsData = useMemo(() => 
    existingMappingsColumns(sourceSiteAbbr, onRemoveMapping, unmappedExpenses.length === 0), 
    [sourceSiteAbbr, onRemoveMapping, unmappedExpenses.length]);

  const unmappedExpensesColumnsData = useMemo(() => 
    unmappedExpensesColumns(sourceSiteAbbr, mappings, handleMappingChange, availableExpenses), 
    [sourceSiteAbbr, mappings, handleMappingChange, availableExpenses]);

  const canSave = useMemo(() => {
    return Object.values(mappings).some(value => value !== '');
  }, [mappings]);

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth="md"
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title={`จับคู่รายการค่าใช้จ่าย - ${sourceSiteAbbr}`}
        onDeleteClick={null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 0}}>

        {existingMappings.length > 0 && (
          <Box >
            <Typography variant="h6" gutterBottom>รายการจับคู่ค่าใช้จ่าย:</Typography>
            <Box sx={{ height: 400, mb: 2 }}>
              <DataGridCellExpand
                hideFooter
                rows={addIdForDataGrid(existingMappings, "sourceExpId")}
                columns={existingMappingsColumnsData}
              />
            </Box>
          </Box>
        )}

        {unmappedExpenses.length > 0 && (
          <BoxFC>
            <Typography variant="h6">
              พบรายการค่าใช้จ่ายที่ไม่สามารถจับคู่อัตโนมัติได้ กรุณาเลือกรายการที่ต้องการจับคู่
            </Typography>
            {/* <Typography variant="subtitle2" gutterBottom>รายการที่ต้องการจับคู่:</Typography> */}
            <Box sx={{ height: 200 }}>
              <DataGridCellExpand
                hideFooter
                rows={addIdForDataGrid(unmappedExpenses, "sourceExpId")}
                columns={unmappedExpensesColumnsData}
              />
            </Box>
          </BoxFC>
        )}


        {unmappedExpenses.length === 0 && existingMappings.length === 0 && (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              ยังไม่มีการแมปรายการค่าใช้จ่ายสำหรับ {sourceSiteAbbr}
            </Typography>
          </Box>
        )}
        </BoxFC>
      </DialogContent>

      <DialogFooter okText="บันทึกการจับคู่"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={unmappedExpenses.length > 0 ? handleSave : null} 
        okDisabled={!canSave} />
    </Dialog>
  )
}

export default React.memo(ExpenseMappingDialog)
